import { useState } from 'react';
import { BellOff, CheckCheck, Loader2, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { normalizeApiError } from '@/services/httpError';
import {
	useMarkAllNotificationsRead,
	useMarkNotificationRead,
	useNotificationList,
	type NotificationFilter,
} from '../hooks/useNotifications';
import type { AppNotification } from '../types';
import { NotificationItem } from './NotificationItem';

interface NotificationPanelProps {
	unreadCount: number;
	/** Close the popover (after following a link). */
	onClose: () => void;
}

/** Content of the bell popover: filter tabs, the list and its actions. */
export function NotificationPanel({
	unreadCount,
	onClose,
}: NotificationPanelProps) {
	const [filter, setFilter] = useState<NotificationFilter>('all');
	const list = useNotificationList(filter);
	const markRead = useMarkNotificationRead();
	const markAllRead = useMarkAllNotificationsRead();
	const navigate = useNavigate();

	const items = list.data?.pages.flat() ?? [];

	const open = (notification: AppNotification, link: string | null) => {
		if (notification.readAt === null) markRead.mutate(notification.id);
		if (!link) return;
		onClose();
		navigate(link);
	};

	return (
		<div className="flex max-h-[min(32rem,75vh)] flex-col">
			<div className="flex items-center justify-between gap-2 border-b px-4 py-3">
				<h2 className="text-sm font-semibold">Notifications</h2>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					onClick={() => markAllRead.mutate()}
					disabled={unreadCount === 0 || markAllRead.isPending}
				>
					{markAllRead.isPending ? (
						<Loader2 className="animate-spin" aria-hidden="true" />
					) : (
						<CheckCheck aria-hidden="true" />
					)}
					Mark all as read
				</Button>
			</div>

			<Tabs
				value={filter}
				onValueChange={(value) => setFilter(value as NotificationFilter)}
				className="px-4 pt-3"
			>
				<TabsList className="w-full">
					<TabsTrigger value="all">All</TabsTrigger>
					<TabsTrigger value="unread">
						Unread{unreadCount > 0 ? ` (${unreadCount})` : ''}
					</TabsTrigger>
				</TabsList>
			</Tabs>

			<div className="mt-2 min-h-0 flex-1 overflow-y-auto pb-2">
				{list.isPending ? (
					<div className="grid gap-2 px-4 py-2" aria-busy="true">
						{Array.from({ length: 4 }, (_, index) => (
							<Skeleton key={index} className="h-14 w-full" />
						))}
					</div>
				) : list.isError ? (
					<div className="grid justify-items-center gap-2 px-4 py-8 text-center text-sm">
						<p className="text-destructive">
							{normalizeApiError(list.error).message}
						</p>
						<Button
							type="button"
							size="sm"
							variant="outline"
							onClick={() => void list.refetch()}
						>
							<RefreshCw aria-hidden="true" />
							Try again
						</Button>
					</div>
				) : items.length === 0 ? (
					<div className="text-muted-foreground grid justify-items-center gap-2 px-4 py-10 text-center text-sm">
						<BellOff className="size-5" aria-hidden="true" />
						{filter === 'unread'
							? "You're all caught up."
							: 'No notifications yet.'}
					</div>
				) : (
					<>
						<ul className="divide-y">
							{items.map((notification) => (
								<NotificationItem
									key={notification.id}
									notification={notification}
									onOpen={open}
									onMarkRead={(item) => markRead.mutate(item.id)}
								/>
							))}
						</ul>
						{list.hasNextPage ? (
							<div className="px-4 pt-2">
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="w-full"
									onClick={() => void list.fetchNextPage()}
									disabled={list.isFetchingNextPage}
								>
									{list.isFetchingNextPage ? (
										<Loader2 className="animate-spin" aria-hidden="true" />
									) : null}
									Load more
								</Button>
							</div>
						) : null}
					</>
				)}
			</div>
		</div>
	);
}
